<template>
  <resource-reader
    v-if="selectedId"
    :resource-id="selectedId"
    :can-acknowledge="canAcknowledge"
    @close="selectedId = null"
  />
  <template v-else>
    <section class="sp-panel">
      <div class="sp-row sp-between">
        <div>
          <h2>Benefits & Holidays</h2>
          <p class="sp-muted">
            Plan your time away and find current benefit information.
          </p>
        </div>
        <a v-if="data" class="sp-link" :href="localUrl(data.leaveUrl)"
          >View my leave</a
        >
      </div>
      <portal-feedback :loading="loading" :error="error" retry @retry="load" />
      <form class="sp-row" @submit.prevent="load">
        <label class="sp-field"
          >Calendar year<input
            v-model.number="year"
            type="number"
            min="2000"
            max="2100"
            required /></label
        ><oxd-button
          type="submit"
          label="View year"
          display-type="secondary"
          :disabled="loading"
        />
      </form>
      <template v-if="data">
        <div class="sp-row sp-between">
          <h3>{{ data.year }} company holidays</h3>
          <a
            v-if="resourceUrl(data.calendarUrl)"
            class="sp-link"
            :href="resourceUrl(data.calendarUrl)"
            download
            >Download holiday calendar (.ics)</a
          >
        </div>
        <div v-if="data.holidays.length" class="sp-table-wrap">
          <table class="sp-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Holiday</th>
                <th scope="col">Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(holiday, index) in data.holidays"
                :key="`${holiday.date}-${index}`"
              >
                <td>{{ displayDate(holiday.date) }}</td>
                <td>{{ holiday.name }}</td>
                <td>{{ holidayLength(holiday.length) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="sp-empty">
          No company holidays have been entered for {{ data.year }}.
        </p>
        <p v-for="(note, index) in data.notes" :key="index" class="sp-notice">
          {{ note }}
        </p>
      </template>
    </section>
    <section v-if="data" class="sp-panel">
      <h2>Benefit resources</h2>
      <p class="sp-muted">
        Read the published policies and official plan documents for benefit
        details.
      </p>
      <p v-if="data.contact" class="sp-notice">
        Questions about benefits? Contact {{ data.contact.name }} through
        <a :href="localUrl('/staff/requests')">HR Requests</a>.
      </p>
      <div v-if="!data.resources.length" class="sp-empty">
        Benefit documents have not been published yet. HR can help with your
        questions.
      </div>
      <div class="sp-grid">
        <article
          v-for="resource in data.resources"
          :key="resource.id"
          class="sp-card"
        >
          <p class="sp-eyebrow">Version {{ resource.version }}</p>
          <h3>{{ resource.title }}</h3>
          <p class="sp-muted">{{ resource.summary }}</p>
          <div class="sp-actions">
            <oxd-button
              label="Read resource"
              display-type="secondary"
              @click="selectedId = resource.id"
            />
          </div>
        </article>
      </div>
    </section>
  </template>
</template>

<script>
import {ref, onMounted} from 'vue';
import PortalFeedback from '../components/PortalFeedback.vue';
import ResourceReader from '../components/ResourceReader.vue';
import {
  portalApi,
  errorMessage,
  displayDate,
  localUrl,
  resourceUrl,
} from '../portal';
export default {
  components: {PortalFeedback, ResourceReader},
  setup() {
    const api = portalApi('benefits');
    const year = ref(
      Number(
        new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          timeZone: 'America/New_York',
        }).format(new Date()),
      ),
    );
    const data = ref(null),
      loading = ref(false),
      error = ref(''),
      selectedId = ref(null),
      canAcknowledge = ref(false);
    let generation = 0;
    async function load() {
      if (
        !Number.isInteger(Number(year.value)) ||
        year.value < 2000 ||
        year.value > 2100
      ) {
        error.value = 'Choose a calendar year between 2000 and 2100.';
        return;
      }
      const request = ++generation;
      loading.value = true;
      error.value = '';
      data.value = null;
      try {
        const result = await api.list({year: Number(year.value)});
        if (request === generation) data.value = result;
      } catch (e) {
        if (request === generation) error.value = errorMessage(e);
      } finally {
        if (request === generation) loading.value = false;
      }
    }
    function holidayLength(length) {
      return length === 4 ? 'Half day' : 'Full day';
    }
    onMounted(() => {
      load();
      portalApi('resources')
        .list({category: 'benefits'})
        .then((result) => {
          canAcknowledge.value = result.canAcknowledge;
        })
        .catch(() => {
          // Resource reading remains available if acknowledgment permission cannot be loaded.
        });
    });
    return {
      year,
      data,
      loading,
      error,
      selectedId,
      canAcknowledge,
      displayDate,
      localUrl,
      resourceUrl,
      holidayLength,
      load,
    };
  },
};
</script>
